import PropTypes from 'prop-types';
import './DataTable.css';

// Paper table that keeps the subset of the MUI DataGrid API the app uses
// (columns with renderCell/valueGetter, server sort + pagination, loading and
// empty states, getRowClassName). Filename kept so the 3 consumers
// (cases-table, notifications-table, billing-invoices) need no change.

function cellValue(col, row) {
  if (typeof col.valueGetter === 'function') {
    return col.valueGetter({ row, value: row[col.field], id: row.id });
  }
  return row[col.field];
}

export default function DataTable({
  columns,
  rows,
  disableSorting = false,
  pageSizeOptions = [5, 10, 25, 50],
  paginationModel,
  onPaginationModelChange,
  sortModel = [],
  onSortModelChange,
  rowCount,
  loading = false,
  hideFooter = false,
  getRowClassName,
  emptyTitle = 'Sin resultados',
  emptyDescription = 'No hay datos para mostrar.',
  hideEmptyState = false,
}) {
  const hasRows = Array.isArray(rows) && rows.length > 0;
  const showEmpty = !hideEmptyState && !loading && !hasRows;
  const sort = sortModel[0];

  const toggleSort = (field) => {
    if (!onSortModelChange) return;
    const isAsc = sort && sort.field === field && sort.sort === 'asc';
    onSortModelChange([{ field, sort: isAsc ? 'desc' : 'asc' }]);
  };

  const page = paginationModel?.page ?? 0;
  const pageSize = paginationModel?.pageSize ?? 10;
  const total = typeof rowCount === 'number' ? rowCount : rows?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const goPage = (p) => onPaginationModelChange?.({ page: p, pageSize });
  const changePageSize = (s) =>
    onPaginationModelChange?.({ page: 0, pageSize: s });

  return (
    <div className={`pdt${loading ? ' pdt--loading' : ''}`}>
      <div className="pdt__scroll">
        <table className="pdt__table">
          <thead>
            <tr>
              {columns.map((col) => {
                const sortable =
                  !disableSorting &&
                  col.sortable !== false &&
                  typeof onSortModelChange === 'function';
                const active = sort && sort.field === col.field;
                return (
                  <th
                    key={col.field}
                    className={`pdt__th${sortable ? ' pdt__th--sortable' : ''}`}
                    style={{
                      width: col.flex ? undefined : col.width,
                      textAlign: col.headerAlign || 'left',
                    }}
                    aria-sort={
                      active
                        ? sort.sort === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                    onClick={sortable ? () => toggleSort(col.field) : undefined}
                  >
                    {col.headerName}
                    {sortable && (
                      <span className="pdt__sort" aria-hidden="true">
                        {active ? (sort.sort === 'asc' ? '▲' : '▼') : '⇅'}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {showEmpty ? (
              <tr>
                <td className="pdt__empty" colSpan={columns.length}>
                  <div className="pdt__empty-title">{emptyTitle}</div>
                  <div className="pdt__empty-desc">{emptyDescription}</div>
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const rowCls = getRowClassName
                  ? getRowClassName({ row, id: row.id })
                  : '';
                return (
                  <tr key={row.id} className={rowCls || undefined}>
                    {columns.map((col) => {
                      const value = cellValue(col, row);
                      const content =
                        typeof col.renderCell === 'function'
                          ? col.renderCell({ value, row, id: row.id })
                          : value;
                      return (
                        <td
                          key={col.field}
                          className="pdt__td"
                          style={{ textAlign: col.align || 'left' }}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!hideFooter && (
        <div className="pdt__footer">
          <label className="pdt__pagesize">
            Filas
            <select
              value={pageSize}
              onChange={(e) => changePageSize(Number(e.target.value))}
            >
              {pageSizeOptions.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>
          <span className="pdt__count">
            {total === 0
              ? '0'
              : `${page * pageSize + 1}–${Math.min(
                  (page + 1) * pageSize,
                  total,
                )} de ${total}`}
          </span>
          <div className="pdt__pager">
            <button
              type="button"
              onClick={() => goPage(page - 1)}
              disabled={page <= 0}
              aria-label="Página anterior"
            >
              {'‹'}
            </button>
            <button
              type="button"
              onClick={() => goPage(page + 1)}
              disabled={page >= totalPages - 1}
              aria-label="Página siguiente"
            >
              {'›'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

DataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  disableSorting: PropTypes.bool,
  pageSizeOptions: PropTypes.array,
  paginationModel: PropTypes.object,
  onPaginationModelChange: PropTypes.func,
  sortModel: PropTypes.array,
  onSortModelChange: PropTypes.func,
  rowCount: PropTypes.number,
  loading: PropTypes.bool,
  hideFooter: PropTypes.bool,
  getRowClassName: PropTypes.func,
  emptyTitle: PropTypes.string,
  emptyDescription: PropTypes.string,
  hideEmptyState: PropTypes.bool,
};
